import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

const AuthChecker = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const tokenCheckIntervalRef = useRef(null);
    const lastTokenRef = useRef(null);

    // List of public pages that don't require authentication
    const PUBLIC_PATHS = [
        '/',
        '/login',
        '/forgot',
        '/otp',
        '/reset-password',
        '/driver-test/',
        '/register'
    ];

    /**
     * Check if a path is public (doesn't require authentication)
     */
    const isPublicPath = (pathname) => {
        return PUBLIC_PATHS.some(publicPath => {
            if (publicPath.endsWith('/')) {
                return pathname.startsWith(publicPath);
            }
            return pathname === publicPath;
        });
    };

    /**
     * Extract token from cookies
     */
    const getTokenFromCookie = () => {
        const cookieString = document.cookie;
        const cookies = cookieString.split(';');
        
        for (let cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'auth_token' && value) {
                return decodeURIComponent(value);
            }
        }
        return null;
    };

    /**
     * Decode JWT token to get expiration time
     */
    const getTokenExpiration = (token) => {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return null;

            // Decode the payload (second part)
            const decoded = JSON.parse(atob(parts[1]));
            
            // JWT uses 'exp' claim for expiration (in seconds since epoch)
            if (decoded.exp) {
                return decoded.exp * 1000; // Convert to milliseconds
            }
            return null;
        } catch (error) {
            console.error('❌ Error decoding token:', error);
            return null;
        }
    };

    /**
     * Check if token is expired
     */
    const isTokenExpired = (token) => {
        if (!token) return true;

        const expirationTime = getTokenExpiration(token);
        if (!expirationTime) return false; // If we can't decode, assume it's valid

        const now = Date.now();
        const isExpired = now > expirationTime;

        if (isExpired) {
            console.log('⏰ Token has expired');
        }

        return isExpired;
    };

    /**
     * Clear authentication data and redirect to login
     */
    const handleLogout = (reason = 'Token expired') => {
        const currentPath = location.pathname;

        // Don't redirect if already on a public page
        if (isPublicPath(currentPath)) {
            return;
        }

        console.log(`🔒 ${reason} - Redirecting to login from ${currentPath}`);

        // Clear all cached data
        queryClient.clear();

        // Clear session storage
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('token_exp');
        localStorage.removeItem('user');

        // Clear the auth cookie (optional - server should do this)
        document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';

        // Redirect to login cleanly
        navigate('/login', { replace: true });
    };

    /**
     * Check token validity
     */
    const checkToken = () => {
        const currentPath = location.pathname;
        const token = getTokenFromCookie();

        // Skip checks on public pages
        if (isPublicPath(currentPath)) {
            return;
        }

        // Token is missing
        if (!token) {
            if (lastTokenRef.current !== null) {
                // Token was present before, now it's gone (cleared by server/user)
                handleLogout('Token cleared');
                lastTokenRef.current = null;
            }
            return;
        }

        lastTokenRef.current = token;

        // Token is expired
        if (isTokenExpired(token)) {
            handleLogout('Session expired');
            return;
        }
    };

    /**
     * Check token on mount and set up interval
     */
    useEffect(() => {

        // Initial check
        checkToken();

        // Set up periodic token checking (every 30 seconds)
        tokenCheckIntervalRef.current = setInterval(() => {
            checkToken();
        }, 30000); // Check every 30 seconds

        // Cleanup interval on unmount
        return () => {
            if (tokenCheckIntervalRef.current) {
                clearInterval(tokenCheckIntervalRef.current);
            }
        };
    }, [location.pathname, navigate, queryClient]);

    /**
     * Listen for storage events (token changes in other tabs)
     */
    useEffect(() => {
        const handleStorageChange = (event) => {
            // Handle logout signal from other tabs
            if (event.key === 'auth_logout' || event.key === 'auth_token_expired') {
                console.log('🔒 Logout signal received from another tab');
                handleLogout('Logged out from another tab');
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [navigate, location, queryClient]);

    /**
     * Handle visibility change - check token when tab becomes visible
     */
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkToken();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [location.pathname, navigate, queryClient]);

    return children;
};

export default AuthChecker;